import * as THREE from 'three/webgpu';

import { SpriteBatch } from '../../render/Billboards.js';
import { STATE, TAU } from '../ai.js';
import { rowFor as defaultRowFor } from '../enemyTypes.js';

/**
 * @file The 2D enemy backend: every enemy of a type is one instance in one
 * {@link SpriteBatch}, i.e. ONE draw call per type no matter how many are alive.
 * Its counterpart is `RigEnemyRenderer`; a creature belongs to exactly one of
 * them for its whole life (`Gameplay` decides, once, from the type's art).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE ATLAS INDEX MEANS
 * ─────────────────────────────────────────────────────────────────────────────
 * `frame = row * cols + col`, frame 0 top-left, row-major — the convention
 * `render/Billboards.js` implements in BOTH of its shader paths and mirrors on
 * the CPU in `frameUVRect`.
 *  • ROW is the yaw facing: `type.sprite.rowFor(enemyYaw, camYaw)`. It depends on
 *    the CAMERA, so it is re-picked every sync — cheap, and correct now that the
 *    camera yaw is free rather than snapped. Authored 2D art is single-row and
 *    returns 0 regardless: a painted creature has no facings.
 *  • COL is the walk cycle, read straight off `manager.animPhase` (the same
 *    phase that drives the tilt/bob), so the drawn frame and the transform
 *    animation are never out of step.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TINTS
 * ─────────────────────────────────────────────────────────────────────────────
 * The atlas art is UNLIT, so the probe sample IS the lighting — `tintD`/`tintN`
 * go in raw (exactly what `actors/Player.js` does with the chibi) and the shader
 * crossfades them by `uNight`. The manager refreshes them on a ~300 ms rolling
 * window; this class never samples anything.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOBODY IN A CAR IS DRAWN HERE
 * ─────────────────────────────────────────────────────────────────────────────
 * An actor with `manager.inCabin` set is SKIPPED — not tinted, not scaled to
 * zero, not written at all. A CAR IS DRAWN EMPTY (owner decision, playtest
 * round 2), and that is the end state of three attempts:
 *
 *  1. the actor's painted chibi, sunk into the car. It clipped out through the
 *     bodywork, because a billboard FACES THE CAMERA and a cabin does not —
 *     any shape sized to fit the glass head-on swings out through the door as
 *     the car yaws;
 *  2. the same quad tinted to a near-black silhouette (round 1). Identical
 *     geometry, identical failure: a black half-body floating beside a moving
 *     saloon reads worse than the painted one did;
 *  3. a small solid HEAD in the glass band. Not a billboard, so it could not
 *     swing out — but a sphere at windscreen height pokes THROUGH the raked
 *     screen, and a head sunk deep enough to avoid it is behind opaque
 *     bodywork and invisible anyway.
 *
 * What is left is nothing, and nothing is correct until a car has a cabin
 * interior worth putting a body in. The slot is otherwise untouched: still
 * live, still shootable, still flashing on a hit, still draggable out of the
 * door by a carjack.
 *
 * ⚠ `inCabin` is NOT the same question as `BRAIN.SEATED` — a carjack victim
 * stays SEATED for the whole drag and IS drawn, because by then his body is
 * outside the car (see `EnemyManager#placeSeated`).
 *
 * ⚠ `object` must be parented under an IDENTITY transform. `SpriteBatch` treats
 * its per-instance positions as WORLD space, and its two backends disagree about
 * what a rotated parent means.
 *
 * @module engine/actors/renderers/SpriteEnemyRenderer
 */

/** @typedef {import('../EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('../../types.js').EnemyType} EnemyType */

export class SpriteEnemyRenderer {
  /**
   * @param {Object} o
   * @param {EnemyType[]} o.types Same array (same order) the manager indexes.
   * @param {import('../../types.js').EngineCaps|null} [o.caps] Selects the
   *   instanced-Sprite path on WebGPU, the billboard fallback on WebGL2.
   * @param {number} [o.max=512] Per-type instance cap; match the manager's.
   * @param {THREE.Object3D|null} [o.parent] Added for you when given.
   * @param {number} [o.alphaTest=0.5] Cutout silhouettes depth-write, so enemies
   *   sort against the world with no render-order bookkeeping.
   * @param {boolean} [o.forceFallback=false] A/B the two billboard paths on one
   *   machine.
   * @param {string} [o.name='enemySprites']
   */
  constructor(o = {}) {
    const {
      types = [],
      caps = null,
      max = 512,
      parent = null,
      alphaTest = 0.5,
      forceFallback = false,
      name = 'enemySprites',
    } = o;

    /** @type {EnemyType[]} */
    this.types = types;
    /** @type {THREE.Group} Parent this under an IDENTITY transform. */
    this.object = new THREE.Group();
    this.object.name = name;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();

    /** @type {Array<SpriteBatch|null>} One batch per type index. */
    this.batches = types.map((t, i) => {
      const sp = t?.sprite;
      if (!sp?.atlas) return null;
      const batch = new SpriteBatch({
        texture: sp.atlas,
        cols: sp.cols ?? 1,
        rows: sp.rows ?? 1,
        max,
        caps,
        worldHeight: sp.worldH ?? t.worldHeight ?? t.height ?? 1.4,
        // Hand-painted atlases have feet on the frame bottom (anchorY 0);
        // ImpostorBaker atlases carry a real anchor (~0.2-0.34) because a
        // pitched projection puts radius*sin(pitch) of content below the
        // origin — without honoring it, baked impostors float above their
        // mesh twins (found by T10's editor overlay).
        anchorY: sp.anchorY ?? 0,
        alphaTest,
        forceFallback,
        name: `${name}:${t.id ?? i}`,
      });
      this.object.add(batch.object);
      return batch;
    });

    /** Per-type write cursor, reset every sync. */
    this._cursor = new Int32Array(Math.max(1, types.length));
    /** Reused `SpriteBatch.set` options — one object for the whole population. */
    this._opts = { scale: 1, frame: 0, rot: 0, tintD: [1, 1, 1], tintN: [0, 0, 0], flash: 0, flipX: false };
    this._td = /** @type {number[]} */ (this._opts.tintD);
    this._tn = /** @type {number[]} */ (this._opts.tintN);
    this._visible = true;
    /**
     * Per-type draw permission, or `null` for "all of them". A creature is 2D
     * art or a 3D model, never both, so this renderer owns every type with a
     * PNG and the mesh renderer owns the rest. Set once at construction.
     * @type {Uint8Array|null}
     */
    this._mask = null;
    this._drawn = 0;
    this._disposed = false;

    parent?.add(this.object);
  }

  /** @returns {'sprite'|'mesh'|'none'} Which billboard path the batches took. */
  get mode() {
    for (const b of this.batches) if (b) return b.mode;
    return 'none';
  }

  /**
   * Rebuilds every instance buffer from the manager's arrays. Called once per
   * FIXED step (see the T6 handoff: enemies commit on the fixed step; render
   * interpolation is a v2 concern).
   *
   * @param {EnemyManager} manager
   * @param {{camYaw?: number, uNightValue?: number}} [o] `uNightValue` is
   *   accepted for signature compatibility and deliberately unused — the shader
   *   does the day/night crossfade from `uNight`, so the CPU must not pre-mix
   *   or the sprites lag the world between probe refreshes.
   */
  sync(manager, o = {}) {
    if (this._disposed) return;
    const camYaw = o.camYaw ?? 0;
    const batches = this.batches;
    const cursor = this._cursor;

    for (let b = 0; b < batches.length; b++) {
      batches[b]?.clear();
      cursor[b] = 0;
    }

    if (this._visible) {
      const opts = this._opts;
      const td = this._td;
      const tn = this._tn;

      const mask = this._mask;
      const inCabin = manager.inCabin;
      for (let i = 0; i < manager.top; i++) {
        if (manager.state[i] === STATE.DEAD) continue;
        // Riding in a car: nothing is drawn for them at all. See the header.
        if (inCabin?.[i]) continue;
        const ti = manager.typeIdx[i];
        if (mask && !mask[ti]) continue;
        const batch = batches[ti];
        if (!batch) continue;
        const type = this.types[ti];
        const sp = type.sprite;

        const yaw = (manager.facing[i] / 256) * TAU;
        const row = (sp.rowFor ?? defaultRowFor)(yaw, camYaw) % sp.rows;
        const cols = sp.cols;
        // The walk phase already runs 0..TAU; slice it into `cols` frames.
        const col = Math.min(cols - 1, (manager.animPhase[i] / TAU) * cols | 0);

        const o3 = i * 3;
        td[0] = manager.tintD[o3];
        td[1] = manager.tintD[o3 + 1];
        td[2] = manager.tintD[o3 + 2];
        tn[0] = manager.tintN[o3];
        tn[1] = manager.tintN[o3 + 1];
        tn[2] = manager.tintN[o3 + 2];

        opts.scale = manager.scale[i];
        opts.frame = row * cols + col;
        opts.rot = manager.wobbleTilt[i];
        opts.flash = manager.flash[i];

        batch.set(cursor[ti]++, manager.x[i], manager.y[i] + manager.wobbleBob[i], manager.z[i], opts);
      }
    }

    let drawn = 0;
    for (const b of batches) {
      if (!b) continue;
      b.commit();
      drawn += b.count;
    }
    this._drawn = drawn;
  }

  /**
   * Hides/shows every sprite creature at once (debug, cutscenes). Routing is
   * {@link setTypeMask}'s job, not this one's.
   * @param {boolean} v
   */
  setVisible(v) {
    this._visible = !!v;
    this.object.visible = this._visible;
  }

  /**
   * Forwards the camera-toward depth bias to every type's batch — the fix for
   * "a wall 40 cm behind a shopkeeper cuts him in half". The mechanism, the
   * measurements and the trade-off live in `render/Billboards.js`'s header;
   * `systems/Gameplay.js` is the only caller and it feeds
   * `cameraDepthBias(rig.yaw, rig.pitch)` every step.
   *
   * Every batch takes it because every batch here is an ACTOR, and the shader
   * scales it by each instance's own world height — so the same vector is right
   * for a chibi and for a two-metre tank without a per-type table.
   *
   * @param {number} [x] @param {number} [y] @param {number} [z] All-zero (the
   *   default) is the un-biased renderer, byte for byte.
   */
  setDepthBias(x = 0, y = 0, z = 0) {
    for (const b of this.batches) b?.setDepthBias(x, y, z);
  }

  /**
   * Restricts drawing to selected type indices.
   * @param {Uint8Array|null} mask One byte per type, non-zero = draw. `null`
   *   restores "every type".
   */
  setTypeMask(mask) {
    this._mask = mask ?? null;
  }

  /** @returns {boolean} True iff at least one type carries a sprite backend. */
  get hasAny() {
    for (const b of this.batches) if (b) return true;
    return false;
  }

  /** @returns {{backend: string, mode: string, drawn: number, draws: number}} */
  stats() {
    let draws = 0;
    for (const b of this.batches) if (b && b.count > 0) draws++;
    return { backend: 'sprite', mode: this.mode, drawn: this._drawn, draws };
  }

  /** Idempotent. Does NOT dispose the atlases — the TYPES own those. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    for (const b of this.batches) b?.dispose();
    this.batches.length = 0;
    this.object.removeFromParent();
  }
}
